import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarPlus, Clock } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SCHEDULE, gcalLink } from "@/constants/site";
import { useLang } from "@/lib/lang";

export default function Schedule() {
  const [active, setActive] = useState("shashthi");
  const { lang, t } = useLang();
  const day = SCHEDULE.find((d) => d.id === active) || SCHEDULE[1];

  return (
    <section id="schedule" className="py-20 sm:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow>{t("sch_eyebrow")}</Eyebrow>
          <h2 className="font-bengali font-extrabold text-3xl sm:text-5xl mt-4">
            {t("sch_title_a")} <span className="gold-text">{t("sch_title_b")}</span>
          </h2>
          <p className="font-bengali text-[#D1BBA7] mt-4">{t("sch_sub")}</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 justify-start sm:justify-center">
            {SCHEDULE.map((d) => (
              <button
                key={d.id}
                data-testid={`schedule-tab-${d.id}`}
                onClick={() => setActive(d.id)}
                className={`shrink-0 rounded-2xl px-5 py-2 text-sm font-bold font-bengali transition-all text-center leading-tight ${
                  active === d.id
                    ? "btn-gold"
                    : "border border-[#F2B134]/25 text-[#D1BBA7] hover:text-[#F2B134] hover:border-[#F2B134]/60"
                }`}
              >
                <span className="block">{lang === "en" ? d.en : d.bn}</span>
                <span className="block text-[10px] font-medium opacity-70 mt-0.5">{lang === "en" ? d.dateEN.split(",")[0] : d.dateBN.split(",")[0]}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={day.id + lang}
            data-testid={`schedule-panel-${day.id}`}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="mt-10 max-w-3xl mx-auto rounded-3xl border border-[#F2B134]/20 bg-[#1E0A0E] p-6 sm:p-10 spotlight"
          >
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-bengali text-3xl sm:text-4xl font-extrabold gold-text">{lang === "en" ? day.en : day.bn}</p>
                <p className="text-sm text-[#D1BBA7] mt-1">{lang === "en" ? day.bn : day.en}</p>
                <p className="font-bengali text-[#FDF6EA] mt-3 text-lg">{lang === "en" ? day.dateEN : day.dateBN}</p>
              </div>
              <a
                href={gcalLink(day)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`schedule-gcal-${day.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-[#F2B134]/30 px-4 py-2 text-xs font-semibold text-[#F2B134] hover:bg-[#F2B134]/10 transition-colors"
              >
                <CalendarPlus size={14} /> {t("sch_gcal")}
              </a>
            </div>

            <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#D7261E]/15 border border-[#D7261E]/40 px-4 py-1.5 text-sm font-bengali text-[#FFB4AE]">
              <Clock size={13} /> {lang === "en" ? day.tithiEN : day.tithiBN}
            </p>

            <div className="mt-8 space-y-0">
              {day.rituals.map((r, i) => (
                <div key={i} className="relative pl-8 pb-8 last:pb-0" data-testid={`schedule-ritual-${day.id}-${i}`}>
                  <span className="absolute left-0 top-1.5 h-3 w-3 rounded-full bg-[#F2B134] shadow-[0_0_12px_rgba(242,177,52,0.8)]" />
                  {i < day.rituals.length - 1 && <span className="absolute left-[5.5px] top-5 bottom-0 w-px bg-[#F2B134]/25" />}
                  <p className="font-bengali font-bold text-[#F2B134]">{lang === "en" ? r.timeEN : r.time}</p>
                  <p className="font-bengali text-[#FDF6EA]/90 mt-1 leading-relaxed">{lang === "en" ? r.textEN : r.text}</p>
                </div>
              ))}
            </div>

            {day.omens && (
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {day.omens.map((o, i) => (
                  <div key={i} className="rounded-xl border border-[#F2B134]/20 bg-[#2B0F15] p-4">
                    <p className="font-bengali font-bold text-[#FDF6EA]">{lang === "en" ? o.textEN : o.text}</p>
                    <p className="font-bengali text-sm text-[#F2B134] mt-1">{lang === "en" ? o.falEN : o.fal}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
